// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { zodResolver } from "@hookform/resolvers/zod";
import { Clock2, ClockPlus, Loader2, Pencil, TriangleAlert } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { z } from "zod";
import { ApiError } from "@/api/api-error";
import {
  type CutOffTimeEntry,
  type CutOffTimesTarget,
  type CutOffUseCase,
  useCreateCutOffTimeMutation,
  useEditCutOffTimeMutation,
} from "@/api/hooks/use-cut-off-times";
import { FormLabelRow } from "@/components/shared/form-label-row";
import { WizardPane } from "@/components/shared/wizard-pane";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
import { formatCutOff, isOnStep, parseDays, roundToStep, STEP_MINUTES } from "./cut-off-times";

const USE_CASES: CutOffUseCase[] = ["reporting", "impact"];

// Abbreviations exactly as `cut_off_times.day_of_week` stores them, in calendar order.
const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"] as const;

// `step` is in seconds; it drives the input's own spinner in quarter hours.
const STEP_SECONDS = STEP_MINUTES * 60;

function createCutOffTimeSchema(t: (key: string) => string) {
  return z.object({
    // <input type="time"> emits `HH:MM`; the API takes a time-of-day and the seconds are added on submit.
    cutOffTime: z
      .string()
      .regex(/^\d{2}:\d{2}$/, t("stores.cutOffTimes.form.validation.cutOffTimeRequired"))
      // `step` alone isn't enough: it constrains the picker but a typed or pasted value still gets
      // through, and a legacy row can seed the form off-grid.
      .refine(isOnStep, t("stores.cutOffTimes.form.validation.cutOffTimeStep")),
    useCase: z.enum(["reporting", "impact"]),
    days: z.array(z.string()),
    department: z.string().optional(),
    taskType: z.string().optional(),
    isDefault: z.boolean(),
  });
}

type CreateCutOffTimeValues = z.infer<ReturnType<typeof createCutOffTimeSchema>>;

interface CreateCutOffTimeDialogProps {
  /** A chain target creates a chain-wide row, which applies to every store in the chain. */
  target: CutOffTimesTarget;
  /** The row being edited, or null/undefined to create a new one. */
  editing?: CutOffTimeEntry | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const EMPTY_VALUES: CreateCutOffTimeValues = {
  cutOffTime: "",
  useCase: "reporting",
  days: [],
  department: "",
  taskType: "",
  isDefault: false,
};

/**
 * Native time input with the browser's own picker suppressed, following the shadcn calendar-time
 * recipe: `appearance-none` plus hiding `::-webkit-calendar-picker-indicator` removes the clock
 * button, so the wheel — which lists every minute regardless of `step` — never opens. What's left is
 * the segmented field you type into, with `step` driving the spinner in quarter hours.
 *
 * Leaving the field snaps a typed value onto the grid, so the usual path never reaches the schema's
 * refine. That refine stays as the backstop for a value that never gets blurred — pasted and
 * submitted straight away, or seeded off-grid from an existing row and left untouched.
 */
function TimeOfDayInput({
  value,
  onChange,
  onBlur,
}: {
  value: string;
  onChange: (v: string) => void;
  onBlur: () => void;
}) {
  return (
    <div className="relative">
      <Input
        type="time"
        step={STEP_SECONDS}
        autoComplete="off"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={() => {
          // Only snap a complete value — a half-typed one should stay as-is for validation to report.
          if (/^\d{2}:\d{2}$/.test(value)) {
            const snapped = roundToStep(value);
            if (snapped !== value) onChange(snapped);
          }
          onBlur();
        }}
        className="pr-9 tabular-nums appearance-none [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-calendar-picker-indicator]:appearance-none"
      />
      <Clock2 className="pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
    </div>
  );
}

/** Seed the form from an existing row when editing. */
function valuesFrom(entry: CutOffTimeEntry): CreateCutOffTimeValues {
  return {
    cutOffTime: formatCutOff(entry.cutOffTime),
    useCase: entry.useCase === "impact" ? "impact" : "reporting",
    days: parseDays(entry.dayOfWeek),
    department: entry.department ?? "",
    taskType: entry.taskType ?? "",
    isDefault: entry.isDefault ?? false,
  };
}

export function CreateCutOffTimeDialog({ target, editing, open, onOpenChange }: CreateCutOffTimeDialogProps) {
  const { t } = useTranslation("back-office");
  const createCutOffTime = useCreateCutOffTimeMutation(target);
  const editCutOffTime = useEditCutOffTimeMutation(target);
  const isChain = target.kind === "chain";
  const isEdit = !!editing;
  const pending = createCutOffTime.isPending || editCutOffTime.isPending;
  const [formError, setFormError] = useState<string | null>(null);

  const schema = useMemo(() => createCutOffTimeSchema(t), [t]);

  const form = useForm<CreateCutOffTimeValues>({
    resolver: zodResolver(schema),
    defaultValues: EMPTY_VALUES,
  });

  // reset() on open re-baselines defaultValues, so isDirty reads as "differs from the row being
  // edited" when editing, and "the blank form has been touched" when creating. Reverting a field to
  // its original value clears it again, so a no-op edit can't be submitted.
  const { isDirty } = form.formState;

  const prevOpen = useRef(false);
  useEffect(
    function seedFormOnDialogOpen() {
      // Seed only when the dialog opens (false → true), not when it closes — resetting on close
      // would visibly blank the fields during the closing animation.
      if (open && !prevOpen.current) {
        form.reset(editing ? valuesFrom(editing) : EMPTY_VALUES);
        setFormError(null);
      }
      prevOpen.current = open;
    },
    [open, form, editing],
  );

  async function onSubmit(values: CreateCutOffTimeValues) {
    setFormError(null);
    const body = {
      cutOffTime: `${values.cutOffTime}:00`,
      useCase: values.useCase,
      // No days picked means the cut off applies to every day, which the column stores as `*`.
      dayOfWeek: values.days.length > 0 ? values.days.join(",") : "*",
      // Omitted rather than sent empty — the API defaults an absent narrowing field to null.
      department: values.department?.trim() || undefined,
      taskType: values.taskType?.trim() || undefined,
      isDefault: values.isDefault,
    };
    try {
      if (editing?.cutOffTimeId != null) {
        await editCutOffTime.mutateAsync({ ...body, cutOffTimeId: editing.cutOffTimeId });
        toast.success(t("stores.cutOffTimes.notifications.updated"));
      } else {
        await createCutOffTime.mutateAsync(body);
        toast.success(t("stores.cutOffTimes.notifications.created"));
      }
      onOpenChange(false);
      form.reset(EMPTY_VALUES);
    } catch (err) {
      // A clash with an existing cut-off is the expected, actionable failure: the backend raises
      // EntityAlreadyExistsError → 409. It's the whole condition that clashes (use case + days +
      // department + task type), not one field, so it reads as a form-level message rather than
      // being anchored to an input. Everything else surfaces inline too, so the dialog never
      // fails silently — the global API toast only covers 422/5xx, and only in debug mode.
      if (err instanceof ApiError && err.status === 409) {
        setFormError(t("stores.cutOffTimes.form.duplicate"));
      } else {
        const fallback = isEdit
          ? t("stores.cutOffTimes.notifications.updateFailed")
          : t("stores.cutOffTimes.notifications.createFailed");
        setFormError(err instanceof ApiError ? err.message : fallback);
      }
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        id="dialog-create-cut-off-time"
        className="sm:max-w-[42rem] p-0 gap-0 overflow-hidden flex flex-col"
        aria-describedby={undefined}
      >
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col">
            <div className="flex flex-row">
              <WizardPane icon={isEdit ? Pencil : ClockPlus} />
              <div className="flex-1 p-6 flex flex-col gap-4 min-w-0">
                <DialogHeader>
                  <DialogTitle>
                    {isEdit ? t("stores.cutOffTimes.form.editTitle") : t("stores.cutOffTimes.form.createTitle")}
                  </DialogTitle>
                </DialogHeader>

                {/* A chain row is the default for every store in the chain — worth stating up front. */}
                {isChain && (
                  <p className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-200">
                    <TriangleAlert className="mt-0.5 size-4 shrink-0" />
                    {t("stores.cutOffTimes.form.chainWideWarning")}
                  </p>
                )}

                <div className="flex gap-4">
                  <FormField
                    control={form.control}
                    name="cutOffTime"
                    render={({ field }) => (
                      <FormItem className="w-36">
                        <FormLabelRow label={t("stores.cutOffTimes.form.cutOffTime")} />
                        <FormControl>
                          <TimeOfDayInput value={field.value} onChange={field.onChange} onBlur={field.onBlur} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="useCase"
                    render={({ field }) => (
                      <FormItem className="flex-1 min-w-0">
                        <FormLabelRow label={t("stores.cutOffTimes.form.useCase")} />
                        <Select value={field.value} onValueChange={field.onChange}>
                          <FormControl>
                            <SelectTrigger className="w-full">
                              <SelectValue />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {USE_CASES.map((u) => (
                              <SelectItem key={u} value={u}>
                                {t(`stores.cutOffTimes.useCases.${u}`, { defaultValue: u })}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="days"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabelRow
                        label={t("stores.cutOffTimes.form.days")}
                        aside={
                          <span className="text-xs leading-none text-muted-foreground">
                            {field.value.length === 0
                              ? t("stores.cutOffTimes.form.everyDayHint")
                              : t("stores.cutOffTimes.form.notRequired")}
                          </span>
                        }
                      />
                      {/* One equal column per day, so the seven buttons share the row width evenly. */}
                      <div className="grid grid-cols-7 gap-1.5">
                        {DAYS.map((day) => {
                          const selected = field.value.includes(day);
                          return (
                            <Button
                              key={day}
                              type="button"
                              variant={selected ? "default" : "outline"}
                              size="sm"
                              aria-pressed={selected}
                              className={cn("w-full min-w-0 px-0", !selected && "text-muted-foreground")}
                              onClick={() =>
                                field.onChange(
                                  selected
                                    ? field.value.filter((d) => d !== day)
                                    : DAYS.filter((d) => d === day || field.value.includes(d)),
                                )
                              }
                            >
                              {t(`stores.cutOffTimes.days.${day.toLowerCase()}`, { defaultValue: day })}
                            </Button>
                          );
                        })}
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="flex gap-4">
                  <FormField
                    control={form.control}
                    name="department"
                    render={({ field }) => (
                      <FormItem className="flex-1 min-w-0">
                        <FormLabelRow
                          label={t("stores.cutOffTimes.form.department")}
                          aside={
                            <span className="text-xs leading-none text-muted-foreground">
                              {t("stores.cutOffTimes.form.notRequired")}
                            </span>
                          }
                        />
                        <FormControl>
                          <Input autoComplete="off" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="taskType"
                    render={({ field }) => (
                      <FormItem className="flex-1 min-w-0">
                        <FormLabelRow
                          label={t("stores.cutOffTimes.form.taskType")}
                          aside={
                            <span className="text-xs leading-none text-muted-foreground">
                              {t("stores.cutOffTimes.form.notRequired")}
                            </span>
                          }
                        />
                        <FormControl>
                          <Input autoComplete="off" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="isDefault"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabelRow label={t("stores.cutOffTimes.form.isDefault")} />
                      <div className="flex items-center gap-2">
                        <FormControl>
                          <Switch id="cut-off-is-default" checked={field.value} onCheckedChange={field.onChange} />
                        </FormControl>
                        <label htmlFor="cut-off-is-default" className="text-sm text-muted-foreground">
                          {t("stores.cutOffTimes.form.isDefaultHint")}
                        </label>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {formError && <p className="text-sm text-destructive">{formError}</p>}
              </div>
            </div>

            <DialogFooter className="mx-0 mb-0 rounded-none rounded-b-lg">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                {t("buttons.cancel")}
              </Button>
              <Button type="submit" disabled={pending || !isDirty}>
                {pending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : isEdit ? (
                  <Pencil className="h-4 w-4" />
                ) : (
                  <ClockPlus className="h-4 w-4" />
                )}
                {isEdit ? t("buttons.save") : t("buttons.create")}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
